"use client";

import { useRef } from "react";
import { Link } from "@repo/next-utils/router";
import { Button } from "@repo/ui/button";
import * as Container from "@repo/ui/container";
import * as Flex from "@repo/ui/flex";
import * as List from "@repo/ui/list";
import { ErrorMessage } from "@/components/error-message/error-message";
import { useFloorContext } from "@/components/floor-context";
import {
  CarouselItem,
  ProductCarousel,
  ProductCarouselProps,
  useProductCarouselControls,
} from "@/components/product-carousel";
import { useSmallViewport } from "@/components/viewport";
import { CAROUSEL } from "@/constants";
import { ChevronLeft } from "@/theme/icons/chevron-left";
import { ChevronRight } from "@/theme/icons/chevron-right";
import {
  TYPOGRAPHY_BODY3,
  TYPOGRAPHY_HEADING1,
  TYPOGRAPHY_HEADING1_BOLD,
  TYPOGRAPHY_HEADING4,
} from "@/theme/topshop/typography/typography-constants";
import classes from "./no-results.module.css";

type NoResultsProps = DefaultProps & {
  searchTerm: string;
};

type DefaultProps = {
  products: ProductCarouselProps["products"];
  country: string;
  currency: string;
  store: string;
};

export const NoResults = ({
  searchTerm,
  products,
  country,
  currency,
  store,
}: NoResultsProps) => {
  const hasTrendingProducts = products?.length;

  return (
    <ErrorMessage
      title={
        <>
          No results for{" "}
          <span className={TYPOGRAPHY_HEADING1_BOLD}>"{searchTerm}"</span>
        </>
      }
      message="Try again with different words or find inspiration from our recommendations below."
    >
      {hasTrendingProducts ? (
        <TrendingProductsSection
          products={products}
          country={country}
          currency={currency}
          store={store}
        />
      ) : null}
    </ErrorMessage>
  );
};

const TrendingProductsSection = ({
  products,
  country,
  currency,
  store,
}: DefaultProps) => {
  const isMobile = useSmallViewport();
  const listRef = useRef<HTMLUListElement>(null);
  const { getTrendingUrl } = useFloorContext();
  const trendingHref = getTrendingUrl();

  const mixedContent: CarouselItem[] = [
    ...(products || []),
    {
      type: "action",
      id: "see-more",
      title: "See More",
      href: trendingHref,
    },
  ];

  const { goNext, goPrev, canGoNext, canGoPrev } = useProductCarouselControls({
    itemCount: mixedContent?.length || 0,
    listContainerRef: listRef,
  });

  return (
    <section
      aria-labelledby="trending-heading"
      className={classes.sectionContainer}
    >
      <div className={classes.header}>
        <Flex.Container align="center" justify="space-between">
          <h2 id="trending-heading" className={TYPOGRAPHY_HEADING1}>
            <Link href={trendingHref}>TRENDING</Link>
          </h2>
          <div className={classes.controlContainer}>
            <Button
              aria-label="Previous product"
              onClick={goPrev}
              size="icon"
              disabled={!canGoPrev}
            >
              <ChevronLeft />
            </Button>
            <Button
              aria-label="Next product"
              onClick={goNext}
              size="icon"
              disabled={!canGoNext}
            >
              <ChevronRight />
            </Button>
          </div>
        </Flex.Container>
      </div>
      <Container.Break asChild containContent>
        <List.Root snap ref={listRef}>
          <ProductCarousel
            products={mixedContent}
            country={country}
            currency={currency}
            store={store}
            sourceSetProfile={CAROUSEL}
            listItemStyle={{
              width: isMobile ? 180 : 300,
            }}
            productPriceClassName={TYPOGRAPHY_BODY3}
            productTitleClassName={TYPOGRAPHY_HEADING4}
            showProductInfo={isMobile ? true : false}
            overlayProductInfo={isMobile ? false : true}
          />
        </List.Root>
      </Container.Break>
    </section>
  );
};
